import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNews } from "@/hooks/content";
import {
  PageHeader,
  StatusBadge,
  ConfirmDialog,
  useToast,
  statusTone,
} from "../components/admin-ui";
import {
  newsCategories,
  newsStatusOptions,
  type AdminArticle,
} from "@/mocks/newsAdmin";

const PAGE_SIZE = 6;

export default function NewsroomPage() {
  const { items, update, remove } = useNews();
  const { show } = useToast();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((a) => {
      if (category && a.category !== category) return false;
      if (status && a.status !== status) return false;
      if (q && !`${a.headline} ${a.author} ${a.excerpt}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, query, category, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const counts = useMemo(
    () => ({
      total: items.length,
      published: items.filter((a) => a.status === "published").length,
      review: items.filter((a) => a.status === "review").length,
      draft: items.filter((a) => a.status === "draft").length,
    }),
    [items],
  );

  function publishNow(a: AdminArticle) {
    update(a.id, { status: "published" });
    show("Article published.");
  }

  const confirmItem = items.find((a) => a.id === confirmId) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Newsroom"
        subtitle="Create, review and publish L+TV news articles."
        actions={
          <button
            type="button"
            onClick={() => navigate("/admin/news/new")}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </span>
            New Article
          </button>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: counts.total, icon: "ri-newspaper-line", tone: "bg-primary-100 text-primary-700" },
          { label: "Published", value: counts.published, icon: "ri-checkbox-circle-line", tone: "bg-green-100 text-green-700" },
          { label: "Pending Review", value: counts.review, icon: "ri-time-line", tone: "bg-accent-100 text-accent-700" },
          { label: "Draft", value: counts.draft, icon: "ri-draft-line", tone: "bg-secondary-100 text-secondary-700" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-lg border border-background-200 p-4">
            <div className="flex items-center justify-between">
              <span className={`w-9 h-9 flex items-center justify-center rounded-md ${s.tone}`}>
                <i className={`${s.icon} text-base`} />
              </span>
              <span className="text-2xl font-bold text-foreground-950">{s.value}</span>
            </div>
            <p className="mt-2 text-xs text-foreground-600">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search headlines, authors…"
            className="w-full pl-9 pr-3 py-2 bg-white border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-white border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="">All categories</option>
          {newsCategories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-white border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="">All statuses</option>
          {newsStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {pageItems.length === 0 ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-newspaper-line text-xl" />
          </div>
          <p className="mt-3 text-sm text-foreground-600">No articles match your filters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
          {pageItems.map((a) => (
            <div key={a.id} className="flex items-start gap-4 px-4 py-4">
              <img
                src={a.image}
                alt={a.headline}
                className="hidden sm:block w-24 h-16 object-cover rounded-md shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {a.breaking && (
                    <StatusBadge tone="live">
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      Breaking
                    </StatusBadge>
                  )}
                  {a.featured && <StatusBadge tone="neutral">Featured</StatusBadge>}
                  <StatusBadge tone={statusTone(a.status)}>{a.status}</StatusBadge>
                  <span className="text-xs text-foreground-500">{a.category}</span>
                </div>
                <h3 className="mt-1 text-sm md:text-base font-semibold text-foreground-950 leading-snug">
                  {a.headline}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">
                  {a.author} ·{" "}
                  {new Date(a.publishedAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {a.status !== "published" && (
                  <button
                    type="button"
                    onClick={() => publishNow(a)}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 border border-green-200 rounded-md hover:bg-green-50 whitespace-nowrap"
                  >
                    <i className="ri-check-line text-sm" />
                    Publish
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`/admin/news/edit/${a.id}`)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-600"
                  aria-label="Edit"
                >
                  <i className="ri-pencil-line text-base" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmId(a.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-accent-50 text-accent-600"
                  aria-label="Delete"
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-foreground-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-sm border border-background-200 rounded-md hover:bg-background-50 disabled:opacity-40 whitespace-nowrap"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-sm border border-background-200 rounded-md hover:bg-background-50 disabled:opacity-40 whitespace-nowrap"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete article?"
        message={
          confirmItem
            ? `Are you sure you want to permanently delete "${confirmItem.headline}"? This action cannot be undone.`
            : ""
        }
        onConfirm={() => {
          if (confirmId) {
            remove(confirmId);
            show("Article deleted.");
          }
          setConfirmId(null);
        }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}